import type { Profile } from '@/lib/queries/profile';

// Canonical post-auth redirect plumbing. Every auth exit funnels its `next`
// through sanitizeNextPath, so a crafted ?next= can never become an open
// redirect. `next` is always an app-relative path; '/' is the sentinel for
// "no meaningful redirect" (which falls through to the normal landing).

const STORAGE_KEY = 'auth.next';

/**
 * Reduce a raw ?next= value to a safe, app-relative path. Anything that is not a
 * plain in-app path — external URL, protocol-relative `//host`, backslash host
 * tricks, encoded variants, control chars, null/empty — collapses to '/'.
 */
export function sanitizeNextPath(raw: string | null | undefined): string {
  if (!raw) return '/';
  let value: string;
  try {
    // Decode once so encoded attacks (e.g. %2F%2Fevil.com) are checked in the
    // same normalized form. A malformed sequence throws → reject.
    value = decodeURIComponent(raw);
  } catch {
    return '/';
  }
  if (!value.startsWith('/')) return '/'; // must be rooted; rejects scheme + bare host
  if (value.startsWith('//')) return '/'; // protocol-relative → external
  if (value.startsWith('/\\')) return '/'; // browsers normalize /\ to // → external
  // Scheme/backslash checks apply to the PATH only, so a benign query survives
  // (e.g. /subscribe?plan=annual). Param values are inert — the destination
  // route reads and validates them; they are never used as a redirect target.
  const path = value.split(/[?#]/, 1)[0] ?? value;
  if (path.includes('\\')) return '/'; // any backslash in the path is a host trick
  if (path.includes('://')) return '/'; // embedded scheme in the path
  for (const ch of value) {
    if (ch.charCodeAt(0) < 0x20) return '/'; // reject control chars anywhere
  }
  return value;
}

/** Persist the sanitized next for the post-auth / onboarding round-trip. */
export function storeNext(raw: string | null | undefined): void {
  const safe = sanitizeNextPath(raw);
  try {
    if (safe !== '/') window.sessionStorage.setItem(STORAGE_KEY, safe);
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* sessionStorage unavailable (private mode) — next is best-effort */
  }
}

/** Read and clear the stored next (consume-once); re-sanitized, never trusted. */
export function takeNext(): string {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    window.sessionStorage.removeItem(STORAGE_KEY);
    return sanitizeNextPath(stored);
  } catch {
    return '/';
  }
}

/**
 * Read the stored next WITHOUT consuming it (re-sanitized). For pre-completion
 * affordances — e.g. showing "your checkout is waiting" during onboarding —
 * that must peek at the destination without disturbing takeNext's consume-once
 * round-trip. Returns '/' when nothing meaningful is stored.
 */
export function peekNext(): string {
  try {
    return sanitizeNextPath(window.sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return '/';
  }
}

/**
 * Build the email/OAuth callback URL, carrying a sanitized next across the
 * round-trip as a query param (sessionStorage does not survive an email opened
 * in another session). callback.tsx re-sanitizes on arrival.
 */
export function callbackUrlWithNext(origin: string, rawNext: string | null | undefined): string {
  const base = `${origin}/auth/callback`;
  const next = sanitizeNextPath(rawNext);
  return next === '/' ? base : `${base}?next=${encodeURIComponent(next)}`;
}

export interface PostAuthDestination {
  /** Where to navigate. */
  to: string;
  /** Whether the caller must storeNext before navigating (onboarding carry). */
  carryNext: boolean;
}

/**
 * The precedence chain at final navigation:
 *   suspended → /suspended (always wins) ▸ recovery → /auth/update-password ▸
 *   onboarding (carry next) ▸ valid next ▸ default landing ('/').
 * Pure so it can be unit-tested without mounting the app.
 */
export function postAuthDestination(args: {
  profile: Pick<Profile, 'suspended_at' | 'onboarding_completed_at' | 'onboarding_path'> | null;
  next: string;
  /** Password-recovery arrival (?intent=recovery) — set the new password first. */
  recovery?: boolean;
}): PostAuthDestination {
  const { profile, next, recovery = false } = args;
  const hasNext = next !== '/';
  // Suspended is terminal and always wins — even over a password recovery.
  if (profile?.suspended_at) return { to: '/suspended', carryNext: false };
  // Recovery beats onboarding and next: the new password is set before landing.
  if (recovery) return { to: '/auth/update-password', carryNext: false };
  // Profile not readable yet (row still provisioning after sign-up): bounce to
  // root so RootIndex waits for it, applies the onboarding gate, and consumes a
  // carried next.
  if (!profile) return { to: '/', carryNext: hasNext };
  // Fresh accounts still route through onboarding; next is carried, not skipped.
  if (!profile.onboarding_completed_at && !profile.onboarding_path) {
    return { to: '/onboarding/welcome', carryNext: hasNext };
  }
  if (hasNext) return { to: next, carryNext: false };
  return { to: '/', carryNext: false }; // default landing, byte-identical to today
}
